import { useEffect, useState } from "react";
import { auth } from "../../firebase/firebase";
import { db, storage } from "../../firebase/firebase";
import {
  collection,
  query,
  where,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { normalizeUrl, isValidHttpUrl } from "../../utils/normalizeUrl";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

function StartupRecords() {
  const user = auth.currentUser;

  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(true);

  const [plan, setPlan] = useState("free");
  const [planLoading, setPlanLoading] = useState(true);

  const [title, setTitle] = useState("");
  const [type, setType] = useState("file");
  const [file, setFile] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;
    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (snap.exists()) setPlan(snap.data().plan || "free");
      setPlanLoading(false);
    });
    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    setRecordsLoading(true);

    const q = query(
      collection(db, "startupRecords"),
      where("userId", "==", user.uid)
    );

    const unsub = onSnapshot(q, (snap) => {
      setRecords(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setRecordsLoading(false);
    });

    return () => unsub();
  }, [user?.uid]);

  const maxAllowed = plan === "paid" ? 10 : 3;
  const limitReached = records.length >= maxAllowed;

  const handleAdd = async () => {
    setError("");

    if (!title.trim()) return setError("Title is required.");
    if (limitReached) {
      return setError(
        plan === "free"
          ? "Free plan allows only 3 startup records."
          : "You have reached the maximum allowed records."
      );
    }

    if (type === "file") {
      if (!file) return setError("Please select a file.");
      if (file.size > MAX_FILE_SIZE)
        return setError("File size must be less than 2 MB.");
    }

    if (type === "link") {
      if (!linkUrl.trim()) return setError("Please provide a link.");
      if (!isValidHttpUrl(linkUrl))
        return setError("Link must start with https://");
    }

    try {
      setSaving(true);
      let fileUrl = null;

      if (type === "file") {
        const storageRef = ref(
          storage,
          `startup-records/${user.uid}/${Date.now()}-${file.name}`
        );
        await uploadBytes(storageRef, file);
        fileUrl = await getDownloadURL(storageRef);
      }

      await addDoc(collection(db, "startupRecords"), {
        userId: user.uid,
        title: title.trim(),
        type,
        fileUrl,
        linkUrl: type === "link" ? normalizeUrl(linkUrl) : null,
        visibility: "public",
        createdAt: serverTimestamp(),
      });

      setTitle("");
      setFile(null);
      setLinkUrl("");
      setType("file");
    } catch {
      setError("Failed to add startup record.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this startup record?")) return;
    await deleteDoc(doc(db, "startupRecords", id));
  };

  return (
    <div>
      <h1
        title="Startup Records are supporting documents or links that help evaluators understand and verify your startup."
      >
        Startup Records
      </h1>

      <p
        style={{ color: "#64748b", maxWidth: 720 }}
        title="These records are visible to evaluators reviewing your startup. Upload only relevant and professional materials."
      >
        Upload official documents or add important links that verify your startup.
        These appear on your public profile for evaluators.
      </p>

      {/* ADD RECORD PANEL */}
      <div
        className="card"
        style={{
          padding: 28,
          marginTop: 28,
          maxWidth: 760,
          background: "linear-gradient(135deg, #f8fafc, #eef2ff)",
        }}
      >
        <h3
          style={{ marginBottom: 14 }}
          title="Add documents or links that strengthen your startup’s credibility."
        >
          Add Startup Record
        </h3>

        <input
          type="text"
          placeholder="Record title (e.g. Incorporation Certificate)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{ width: "100%", marginTop: 8 }}
          title="Use a clear, descriptive title so evaluators immediately understand what this record is."
        />

        <div style={{ marginTop: 16, display: "flex", gap: 20 }}>
          <label
            style={{ fontWeight: 500 }}
            title="Upload a PDF or document such as incorporation papers, contracts, or reports."
          >
            <input
              type="radio"
              checked={type === "file"}
              onChange={() => setType("file")}
            />{" "}
            Upload File
          </label>

          <label
            style={{ fontWeight: 500 }}
            title="Add a secure external link (Google Drive, Notion, website, etc.)"
          >
            <input
              type="radio"
              checked={type === "link"}
              onChange={() => setType("link")}
            />{" "}
            External Link
          </label>
        </div>

        {type === "file" && (
          <input
            type="file"
            onChange={(e) => setFile(e.target.files[0])}
            style={{ marginTop: 14 }}
            title="Maximum file size: 2 MB. Upload only clear, readable documents."
          />
        )}

        {type === "link" && (
          <input
            type="url"
            placeholder="https://drive.google.com/..."
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            style={{ width: "100%", marginTop: 14 }}
            title="Use links that evaluators can access without requesting permissions."
          />
        )}

        {error && <p style={{ color: "#dc2626", marginTop: 10 }}>{error}</p>}

        <button
          className="btn-primary"
          onClick={handleAdd}
          disabled={saving || limitReached || planLoading}
          style={{
            marginTop: 24,
            display: "block",
            padding: "10px 20px",
            borderRadius: 999,
          }}
          title={
            limitReached
              ? "Upgrade your plan to add more startup records."
              : "Add this record to your public startup profile."
          }
        >
          {limitReached
            ? "Upgrade to add more"
            : saving
            ? "Saving..."
            : "Add Record"}
        </button>

        <p
          style={{ marginTop: 10, fontSize: 13, color: "#64748b" }}
          title="Free plan allows up to 3 records. Paid plan allows up to 10."
        >
          {records.length}/{maxAllowed} records used · Max file size: 2 MB
        </p>
      </div>

      {/* RECORD LIST */}
      <div style={{ marginTop: 40, maxWidth: 760 }}>
        <h3 title="These are the records currently visible to evaluators reviewing your startup.">
          Your Startup Records
        </h3>

        {recordsLoading ? (
          <p style={{ marginTop: 10, color: "#64748b" }}>Loading records…</p>
        ) : records.length === 0 ? (
          <div
            className="card"
            style={{
              padding: 22,
              marginTop: 14,
              textAlign: "center",
              color: "#94a3b8",
            }}
            title="Add records to strengthen your startup’s credibility during evaluation."
          >
            No startup records added yet.
          </div>
        ) : (
          records.map((rec) => (
            <div
              key={rec.id}
              className="card"
              style={{
                padding: 18,
                marginTop: 14,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "translateY(-3px)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "translateY(0)")
              }
              title="This record is visible to evaluators."
            >
              <div style={{ fontWeight: 600 }}>{rec.title}</div>

              <a
                href={`/record/${rec.id}`}
                target="_blank"
                rel="noreferrer"
                style={{ display: "inline-block", marginTop: 6 }}
                title="Open this record in a new tab."
              >
                {rec.type === "link" ? "🔗 View link" : "📄 View file"}
              </a>

              <button
                className="btn-secondary"
                style={{ marginLeft: 14 }}
                onClick={() => handleDelete(rec.id)}
                title="Remove this record from your startup profile."
              >
                Delete
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default StartupRecords;
